import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiPost, connectSseStream, type ISseStreamSubscriptionCallbacks } from 'services/api';
import type { TDeviceSyncStreamUrl, IDeviceSyncSuccessRes, IDeviceSyncCompleteReq } from 'types/wms/device-sync.types';

const DEVICE_SYNC_PATH = `${process.env.REACT_APP_API_WMS}/device-sync`;

/**
 * Genera la URL para establecer la conexión Server-Sent Events (SSE) con el dispositivo secundario.
 * * @param sessionId - El identificador único de la sesión de sincronización.
 * @returns La URL completa y codificada para conectarse al flujo de eventos.
 */
export const getDeviceSyncStreamUrl = (sessionId: string): TDeviceSyncStreamUrl => {
  return `${DEVICE_SYNC_PATH}/stream?sessionId=${encodeURIComponent(sessionId)}`;
};

/**
 * Inicia la conexión en tiempo real para la sincronización de dispositivos.
 * * @param sessionId - ID único de la sesión de sincronización.
 * @param callbacks - Manejadores para eventos de apertura, datos y errores.
 * @returns Función de limpieza para cerrar la conexión y liberar recursos.
 */
export const connectDeviceSyncStream = (
  sessionId: string,
  callbacks: ISseStreamSubscriptionCallbacks
): (() => void) => {
  const syncUrl = getDeviceSyncStreamUrl(sessionId);

  return connectSseStream(syncUrl, callbacks);
};

/**
 * Notifica al servidor que el dispositivo secundario ha abierto la sesión (ej. escaneó el QR exitosamente).
 * * @param sessionId - El identificador único de la sesión activa.
 * @returns Una promesa que resuelve con la confirmación de éxito del servidor.
 */
export const postDeviceSyncSessionOpened = async (sessionId: string): Promise<IDeviceSyncSuccessRes> => {
  return apiPost<AxiosRequestConfig, undefined>(
    `${DEVICE_SYNC_PATH}/opened?sessionId=${encodeURIComponent(sessionId)}`,
    undefined
  )
    .then((response: AxiosResponse<IDeviceSyncSuccessRes>) => Promise.resolve(response.data as IDeviceSyncSuccessRes))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Finaliza la sesión de sincronización enviando la evidencia o los datos capturados por el dispositivo secundario.
 * * @param sessionId - El identificador único de la sesión.
 * @param payload - Objeto que contiene los datos o la evidencia capturada durante la sesión.
 * @returns Una promesa que resuelve confirmando que los datos se guardaron y la sesión se completó.
 */
export const postDeviceSyncComplete = async (
  sessionId: string,
  payload: IDeviceSyncCompleteReq
): Promise<IDeviceSyncSuccessRes> => {
  return apiPost<AxiosRequestConfig, IDeviceSyncCompleteReq>(
    `${DEVICE_SYNC_PATH}/complete?sessionId=${encodeURIComponent(sessionId)}`,
    payload
  )
    .then((response: AxiosResponse<IDeviceSyncSuccessRes>) => Promise.resolve(response.data as IDeviceSyncSuccessRes))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Cancela o aborta la sesión de sincronización de forma manual.
 * * @param sessionId - El identificador único de la sesión a cancelar.
 * @returns Una promesa que resuelve confirmando la cancelación exitosa.
 */
export const postDeviceSyncCancel = async (sessionId: string): Promise<IDeviceSyncSuccessRes> => {
  return apiPost<AxiosRequestConfig, undefined>(
    `${DEVICE_SYNC_PATH}/cancel?sessionId=${encodeURIComponent(sessionId)}`,
    undefined
  )
    .then((response: AxiosResponse<IDeviceSyncSuccessRes>) => Promise.resolve(response.data as IDeviceSyncSuccessRes))
    .catch((error: AxiosError) => Promise.reject(error));
};
